import React from 'react';
import { useTable } from 'react-table';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import THead from 'Components/Table/THead';
import TBody from 'Components/Table/TBody';
import { createColumns, createInitialState } from 'Components/Table/utils';
import { OurTableProps } from 'Components/Table/types';

import styles from './index.css';

type MiniTableProps<D extends object> = Pick<
    OurTableProps<D>,
    'data' | 'columns' | 'dataTestId' | 'initialState' | 'noDataMessage'
> & {
    className?: string;
};

const Table = <D extends object>({
    dataTestId,
    data,
    columns,
    initialState = {},
    noDataMessage = 'No data found for this table',
    className = ''
}: MiniTableProps<D>) => {
    const { t } = useTranslation();

    // the initialState shouldn't really change for the table after mounting.
    const tableInitialState = React.useMemo(
        () => createInitialState({ initialState, data, columns, reduxInitialState: {} }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const tableColumns = React.useMemo(() => {
        return createColumns<D>({ columns, dataTestId });
    }, [columns, dataTestId]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<D>({
        columns: tableColumns,
        data,
        initialState: tableInitialState
    });

    return (
        <>
            <div
                data-testid={dataTestId}
                className={clsx('brui_table', className, styles.tableContainer)}
                {...getTableProps()}
            >
                <THead headerGroups={headerGroups} dataTestId={dataTestId} />
                {rows.length < 1 ? (
                    <div className={clsx('noDataMsg', className, styles.tableContainer)}>{t(noDataMessage)}</div>
                ) : (
                    <>
                        <TBody getTableBodyProps={getTableBodyProps} rows={rows} prepareRow={prepareRow} />
                    </>
                )}
            </div>
        </>
    );
};

export default Table;
